import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import api from '../../../api/axios';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.oldPassword) {
      newErrors.oldPassword = 'Old password is required';
    }

    if (!formData?.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.newPassword !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      setSubmitting(true);
      setApiError('');
      try {
        await api.post('/api/v1/settings/change-password', {
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword
        });
        handleClose();
      } catch (err) {
        setApiError(err.response?.data?.message || 'Failed to change password');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setFormData({
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    setApiError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-md animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Lock" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Change Password</h3>
              <p className="text-xs text-muted-foreground">Update your account password</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-lg transition-all hover:scale-110"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* API Error */}
        {apiError && (
          <div className="mx-6 mt-4 p-3 bg-error/10 border border-error rounded-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={16} className="text-error" />
            <p className="text-sm text-error">{apiError}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Old Password"
            type="password"
            required
            value={formData?.oldPassword}
            onChange={(e) => handleInputChange('oldPassword', e?.target?.value)}
            error={errors?.oldPassword}
            placeholder="Enter your current password"
            className="transition-all focus:scale-[1.01]"
          />

          <Input
            label="New Password"
            type="password"
            required
            value={formData?.newPassword}
            onChange={(e) => handleInputChange('newPassword', e?.target?.value)}
            error={errors?.newPassword}
            placeholder="Enter new password (min 8 characters)"
            className="transition-all focus:scale-[1.01]"
          />

          <Input
            label="Confirm Password"
            type="password"
            required
            value={formData?.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
            error={errors?.confirmPassword}
            placeholder="Re-enter new password"
            className="transition-all focus:scale-[1.01]"
          />

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 hover:scale-[1.02] transition-all"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Check"
              iconPosition="left"
              disabled={submitting}
              className="flex-1 hover:scale-[1.02] transition-all"
            >
              {submitting ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal;